import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useParams } from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowUp,
  faCamera,
  faPlus,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { useAuthContext } from '../contexts/AuthContext';
import Page404 from './404';
import api from "../lib/request.js";
import config from "../config.js";
import HeaderNavbar from '../components/HeaderNavbar';
import Footer from '../components/Footer';
import { buildAuthenticatedWebSocketURL } from '../lib/websocket';
import './GuideUpload.css';

const REMOTE_STEP_ID_PATTERN = /^[A-Za-z0-9._-]{1,128}$/;

function sanitizeRemoteText(value, maxLength = 280) {
  const text = (value || '').toString().trim();
  return text.slice(0, maxLength);
}

function normalizeRemoteStep(rawStep, index) {
  if (!rawStep || typeof rawStep !== 'object') return null;
  const rawStepId = sanitizeRemoteText(rawStep.stepId, 128);
  const position = Number.parseInt(rawStep.position, 10);
  if (!REMOTE_STEP_ID_PATTERN.test(rawStepId)) return null;
  if (!Number.isFinite(position) || position < 1) return null;
  return {
    stepId: rawStepId,
    position,
    title: sanitizeRemoteText(rawStep.title, 240) || `Step ${position || index + 1}`
  };
}

function normalizeRemoteGuideState(rawState) {
  const steps = Array.isArray(rawState?.steps)
    ? rawState.steps.map((step, index) => normalizeRemoteStep(step, index)).filter(Boolean).slice(0, 300)
    : [];
  const rawStepId = sanitizeRemoteText(rawState?.currentStepId, 128);
  const currentStepId = REMOTE_STEP_ID_PATTERN.test(rawStepId) ? rawStepId : '';
  const currentStepPosition = Number.parseInt(rawState?.currentStepPosition, 10);
  const matchedStep = currentStepId
    ? steps.find((step) => step.stepId === currentStepId)
    : steps.find((step) => step.position === currentStepPosition);
  const selectedStep = matchedStep || steps[0] || null;

  return {
    editorEditable: rawState?.editorEditable === true,
    steps,
    currentStepId: selectedStep?.stepId || null,
    currentStepPosition: selectedStep?.position || null,
    currentStepTitle: selectedStep?.title || sanitizeRemoteText(rawState?.currentStepTitle, 240),
    stepCount: steps.length,
    canAddStep: rawState?.canAddStep === true,
    canDeleteCurrent: rawState?.canDeleteCurrent === true
  };
}

function GuideHomeUpload() {
  const { isAuthenticated, canAdminOrganization } = useAuthContext();
  const { project, guide } = useParams();

  const [statusCode, setStatusCode] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [requireLogin, setRequireLogin] = useState(false);
  const [uploadResult, setUploadResult] = useState({ complete: false });
  const [socketUrl, setSocketUrl] = useState(null);
  const [socketRetryKey, setSocketRetryKey] = useState(0);
  const [liveGuideState, setLiveGuideState] = useState(null);
  const [pendingAction, setPendingAction] = useState('');
  const [sessionAlert, setSessionAlert] = useState(null);
  const processedSocketMessageRef = useRef(null);
  const fileInputRef = useRef(null);
  const socketReconnectTimerRef = useRef(null);
  const socketRequestIdRef = useRef(0);
  const shouldMaintainSocketRef = useRef(false);
  const socketAccessDeniedRef = useRef(false);

  const canManageGuide = isAuthenticated && data?.project?.org !== undefined && canAdminOrganization(data.project.org);

  useEffect(() => {
    setLiveGuideState(null);
    setPendingAction('');
    setSessionAlert(null);
    setUploadResult({ complete: false });
    processedSocketMessageRef.current = null;
  }, [project, guide]);

  useEffect(() => () => {
    if (socketReconnectTimerRef.current) {
      window.clearTimeout(socketReconnectTimerRef.current);
      socketReconnectTimerRef.current = null;
    }
  }, []);

  function clearSocketReconnectTimer() {
    if (socketReconnectTimerRef.current) {
      window.clearTimeout(socketReconnectTimerRef.current);
      socketReconnectTimerRef.current = null;
    }
  }

  function scheduleSocketReconnect() {
    if (!shouldMaintainSocketRef.current) {
      return;
    }
    clearSocketReconnectTimer();
    socketReconnectTimerRef.current = window.setTimeout(() => {
      if (!shouldMaintainSocketRef.current) {
        return;
      }
      setSocketRetryKey((prev) => prev + 1);
    }, 1000);
  }

  useEffect(() => {
    const shouldMaintain = isAuthenticated && canManageGuide;
    shouldMaintainSocketRef.current = shouldMaintain;

    if (!shouldMaintain) {
      clearSocketReconnectTimer();
      socketRequestIdRef.current += 1;
      socketAccessDeniedRef.current = false;
      setSocketUrl(null);
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const requestId = ++socketRequestIdRef.current;

    (async () => {
      try {
        const nextUrl = await buildAuthenticatedWebSocketURL(config.wsURL, {
          project,
          guide,
          remoteUploader: true,
          signal: controller.signal
        });
        if (!nextUrl || cancelled || controller.signal.aborted || socketRequestIdRef.current !== requestId || !shouldMaintainSocketRef.current) {
          return;
        }
        socketAccessDeniedRef.current = false;
        setSessionAlert(null);
        setSocketUrl(nextUrl);
      } catch (error) {
        if (cancelled || controller.signal.aborted || socketRequestIdRef.current !== requestId) {
          return;
        }
        setSocketUrl(null);
        setPendingAction('');
        if (error?.status === 403) {
          if (!socketAccessDeniedRef.current) {
            socketAccessDeniedRef.current = true;
            setSessionAlert({
              variant: 'danger',
              message: 'Your access to this guide uploader is no longer available. Refresh with an authorized account to reconnect.'
            });
          }
          return;
        }
        if (error?.status === 401) {
          return;
        }
        clearSocketReconnectTimer();
        socketReconnectTimerRef.current = window.setTimeout(() => {
          if (!shouldMaintainSocketRef.current) {
            return;
          }
          setSocketRetryKey((prev) => prev + 1);
        }, 1000);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [project, guide, isAuthenticated, canManageGuide, socketRetryKey]);

  const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(socketUrl, {
    onClose: () => {
      setPendingAction('');
      if (!shouldMaintainSocketRef.current) {
        return;
      }
      setSocketUrl(null);
      scheduleSocketReconnect();
    },
    onError: () => {
      if (!shouldMaintainSocketRef.current) {
        return;
      }
      setSocketUrl(null);
      scheduleSocketReconnect();
    },
    shouldReconnect: () => false
  });

  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      clearSocketReconnectTimer();
    }
    if (readyState !== ReadyState.OPEN) {
      if (readyState === ReadyState.CLOSING || readyState === ReadyState.CLOSED) {
        setPendingAction('');
      }
      return;
    }
    sendJsonMessage({ cmd: 'remoteRequestGuideState' });
  }, [readyState, sendJsonMessage]);

  const currentStepInfo = useMemo(() => {
    const steps = Array.isArray(liveGuideState?.steps) ? liveGuideState.steps : [];
    if (!steps.length) return null;
    if (liveGuideState?.currentStepId) {
      return steps.find((step) => step.stepId === liveGuideState.currentStepId) || steps[0];
    }
    if (liveGuideState?.currentStepPosition) {
      return steps.find((step) => step.position === liveGuideState.currentStepPosition) || steps[0];
    }
    return steps[0];
  }, [liveGuideState]);

  const currentStepIndex = currentStepInfo
    ? (liveGuideState?.steps || []).findIndex((step) => step.stepId === currentStepInfo.stepId)
    : -1;
  const hasLiveEditorSession = liveGuideState?.editorEditable === true;
  const hasLiveSteps = Array.isArray(liveGuideState?.steps) && liveGuideState.steps.length > 0;
  const controlsDisabled = loading || !!pendingAction || readyState !== ReadyState.OPEN || !hasLiveEditorSession;
  const canUpload = !controlsDisabled && !!currentStepInfo;
  const canAddStep = !controlsDisabled && (liveGuideState?.canAddStep === true || !hasLiveSteps);
  const canDeleteCurrentStep = !controlsDisabled && !!currentStepInfo && liveGuideState?.canDeleteCurrent === true;
  const canMoveUp = !controlsDisabled && currentStepIndex > 0;
  const canMoveDown = !controlsDisabled && currentStepIndex > -1 && currentStepIndex < (liveGuideState?.steps?.length || 0) - 1;

  const connectionStatus = {
    [ReadyState.CONNECTING]: 'Connecting',
    [ReadyState.OPEN]: 'Connected',
    [ReadyState.CLOSING]: 'Closing',
    [ReadyState.CLOSED]: 'Disconnected',
    [ReadyState.UNINSTANTIATED]: 'Starting'
  }[readyState];

  const sessionStatus = useMemo(() => {
    if (readyState === ReadyState.CONNECTING || readyState === ReadyState.UNINSTANTIATED) {
      return {
        tone: 'warning',
        label: connectionStatus,
        message: 'Connecting to the live guide editor session.'
      };
    }
    if (readyState === ReadyState.CLOSING || readyState === ReadyState.CLOSED) {
      return {
        tone: 'error',
        label: connectionStatus,
        message: 'Realtime sync is unavailable. The uploader will keep trying to reconnect.'
      };
    }
    if (!liveGuideState) {
      return {
        tone: 'warning',
        label: 'Waiting for editor',
        message: 'Open this guide in edit mode on the main device to start uploading and managing steps from here.'
      };
    }
    if (liveGuideState.editorEditable !== true) {
      return {
        tone: 'warning',
        label: 'Editor unavailable',
        message: 'The main guide editor is no longer active in edit mode.'
      };
    }
    if (!liveGuideState.steps.length) {
      return {
        tone: 'success',
        label: 'Live session ready',
        message: 'This guide has no steps yet. Create the first step below and then upload images to it.'
      };
    }
    return {
      tone: 'success',
      label: 'Live session ready',
      message: 'Select the target step, add or delete steps, and upload images from this device.'
    };
  }, [connectionStatus, liveGuideState, readyState]);

  function getStatusPillClassName() {
    if (sessionStatus.tone === 'success') return 'guide-upload-status-pill';
    if (sessionStatus.tone === 'warning') return 'guide-upload-status-pill is-warning';
    return 'guide-upload-status-pill is-error';
  }

  const fetchData = useCallback(async () => {
    try {
      const response = await api.get(`/api/v1/projects/${project}/guides/${guide}`);
      setData(response);
      setStatusCode(null);
    } catch (e) {
      if (e?.statusCode === 404) {
        if (isAuthenticated) {
          setStatusCode('404');
        } else {
          setRequireLogin(true);
        }
      }
      setData(null);
    }
  }, [guide, isAuthenticated, project]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (lastJsonMessage === null) {
      return;
    }
    if (processedSocketMessageRef.current === lastJsonMessage) {
      return;
    }
    processedSocketMessageRef.current = lastJsonMessage;

    switch (lastJsonMessage.cmd) {
      case 'remoteGuideState':
        setLiveGuideState(normalizeRemoteGuideState(lastJsonMessage));
        setPendingAction('');
        setSessionAlert(null);
        break;
      case 'movedToStep':
        setLiveGuideState((prevState) => {
          if (!prevState || !Array.isArray(prevState.steps) || !prevState.steps.length) {
            return prevState;
          }
          const rawStepId = sanitizeRemoteText(lastJsonMessage.stepId, 128);
          const requestedStepId = REMOTE_STEP_ID_PATTERN.test(rawStepId) ? rawStepId : '';
          const requestedPosition = Number.parseInt(lastJsonMessage.step, 10);
          const matchedStep = requestedStepId
            ? prevState.steps.find((step) => step.stepId === requestedStepId)
            : prevState.steps.find((step) => step.position === requestedPosition);
          if (!matchedStep) {
            return prevState;
          }
          return {
            ...prevState,
            currentStepId: matchedStep.stepId,
            currentStepPosition: matchedStep.position,
            currentStepTitle: matchedStep.title
          };
        });
        setPendingAction('');
        break;
      case 'editorUnavailable':
        setPendingAction('');
        setLiveGuideState((prevState) => {
          if (!prevState) {
            return normalizeRemoteGuideState({ editorEditable: false, steps: [] });
          }
          return {
            ...prevState,
            editorEditable: false
          };
        });
        setSessionAlert({
          variant: 'warning',
          message: 'The main guide editor closed or left edit mode. Reopen edit mode on the main device to continue.'
        });
        break;
      case 'remoteActionError':
        setPendingAction('');
        setSessionAlert({
          variant: 'danger',
          message: sanitizeRemoteText(lastJsonMessage?.message, 280) || 'The main guide editor could not apply that change.'
        });
        break;
      default:
        break;
    }
  }, [lastJsonMessage]);

  function sendRemoteCommand(cmd, payload, pendingLabel) {
    if (readyState !== ReadyState.OPEN) {
      setSessionAlert({
        variant: 'danger',
        message: 'Realtime sync is unavailable. Wait for the uploader to reconnect.'
      });
      return;
    }
    setPendingAction(pendingLabel);
    setSessionAlert(null);
    sendJsonMessage({ cmd, ...payload });
  }

  function handleMoveSelection(direction) {
    if (!currentStepInfo || !Array.isArray(liveGuideState?.steps)) return;
    const nextIndex = direction === 'up' ? currentStepIndex - 1 : currentStepIndex + 1;
    const nextStep = liveGuideState.steps[nextIndex];
    if (!nextStep?.stepId) return;
    sendRemoteCommand(
      'remoteSelectStep',
      { stepId: nextStep.stepId },
      direction === 'up' ? 'Moving to the previous step...' : 'Moving to the next step...'
    );
  }

  function handleAddStepBelow() {
    const payload = currentStepInfo?.stepId ? { stepId: currentStepInfo.stepId } : {};
    const pendingLabel = hasLiveSteps ? 'Adding a new step...' : 'Creating the first step...';
    sendRemoteCommand('remoteAddStepBelow', payload, pendingLabel);
  }

  function handleDeleteCurrentStep() {
    if (!currentStepInfo?.stepId) return;
    const stepLabel = currentStepInfo.title || `Step ${currentStepInfo.position}`;
    const confirmed = window.confirm(`Delete ${stepLabel}? This removes the step from the current guide draft.`);
    if (!confirmed) return;
    sendRemoteCommand(
      'remoteDeleteStep',
      { stepId: currentStepInfo.stepId },
      'Deleting the current step...'
    );
  }

  async function handleChangeFile(event) {
    const targetStep = currentStepInfo;
    if (!event?.target) return;

    const files = Array.from(event.target.files || []).filter((file) => file?.type?.startsWith('image/'));
    if (!targetStep?.stepId) {
      setUploadResult({
        complete: true,
        class: 'danger',
        message: 'Wait for the live guide editor to select a step before uploading images.'
      });
      event.target.value = '';
      return;
    }
    if (!files.length) {
      setUploadResult({
        complete: true,
        class: 'danger',
        message: 'Only image files can be uploaded.'
      });
      event.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('step', targetStep.position);
    formData.append('stepTitle', targetStep.title || `Step ${targetStep.position}`);
    formData.append('stepId', targetStep.stepId);
    formData.append('remote', true);

    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    setLoading(true);
    setSessionAlert(null);

    try {
      const response = await api({
        method: 'post',
        url: `/api/v1/projects/${project}/guides/${guide}/upload`,
        data: formData,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response?.ok) {
        setUploadResult({
          complete: true,
          class: 'success',
          message: `Upload complete for Step ${targetStep.position}.`
        });
      } else {
        setUploadResult({
          complete: true,
          class: 'danger',
          message: response?.message || 'Unable to upload images.'
        });
      }
    } catch (err) {
      setUploadResult({
        complete: true,
        class: 'danger',
        message: err?.message || 'Unable to upload images.'
      });
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  }

  function showFileDialog() {
    setUploadResult({ complete: false });
    if (!canUpload) {
      setSessionAlert({
        variant: 'warning',
        message: 'The uploader needs an active main editor session and a selected step before images can be uploaded.'
      });
      return;
    }
    fileInputRef.current?.click();
  }

  function getGoBack() {
    return window.encodeURI(`/projects/${project}/guides/${guide}/remote-upload`);
  }

  if (statusCode === '404') {
    return (<Page404 />);
  }

  if (requireLogin) {
    return (<Navigate to={`${config.prefixPath}/login?goback=${getGoBack()}&rl=1`} />);
  }

  if (!data) {
    return (
      <>
        <HeaderNavbar allowAdminContent={false} />
        <div className="guide-upload-page">
          <div className="guide-upload-shell">
            <div className="guide-upload-loading-state">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (!isAuthenticated) {
    return (<Navigate to={`${config.prefixPath}/login?goback=${getGoBack()}&rl=1`} />);
  }

  if (!canManageGuide) {
    return (
      <>
        <HeaderNavbar allowAdminContent={false} />
        <div className="guide-upload-page">
          <div className="guide-upload-shell">
            <div className="alert alert-danger" role="alert">
              You do not have permissions to upload images for this guide.
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <HeaderNavbar allowAdminContent={false} />
      <div className="guide-upload-page">
        <div className="guide-upload-shell">
          <section className="guide-upload-card guide-upload-hero">
            <p className="guide-upload-eyebrow">Remote Uploader</p>
            <h1 className="guide-upload-headline">Upload guide images from another device</h1>
            <p className="guide-upload-copy">
              Keep the main guide editor open in edit mode on the other device. This page stays synced to the live editing session and can change the active step, add steps, or delete the current step before you upload images.
            </p>
          </section>

          <section className="guide-upload-card">
            <div className="guide-upload-meta-grid">
              <div>
                <div className="guide-upload-meta-label">Project</div>
                <div className="guide-upload-meta-value">{data.project.title}</div>
                {data.project.subTitle ? (
                  <div className="guide-upload-meta-subvalue">{data.project.subTitle}</div>
                ) : null}
              </div>
              <div>
                <div className="guide-upload-meta-label">Guide</div>
                <div className="guide-upload-meta-value">{data.guide.title}</div>
              </div>
            </div>
          </section>

          <section className="guide-upload-card">
            <div className="guide-upload-status-row">
              <div>
                <div className="guide-upload-meta-label">Session status</div>
                <div className="guide-upload-meta-subvalue">
                  {pendingAction ? pendingAction : sessionStatus.message}
                </div>
              </div>
              <span className={getStatusPillClassName()}>{sessionStatus.label}</span>
            </div>
          </section>

          {sessionAlert ? (
            <div className={`alert alert-${sessionAlert.variant}`} role="alert">
              {sessionAlert.message}
            </div>
          ) : null}

          <section className="guide-upload-card">
            <div className="guide-upload-target-row">
              <div>
                <div className="guide-upload-meta-label">Current upload target</div>
                {currentStepInfo ? (
                  <>
                    <span className="guide-upload-target-badge">
                      Step {currentStepInfo.position}
                      {liveGuideState?.stepCount ? ` of ${liveGuideState.stepCount}` : ''}
                    </span>
                    <h2 className="guide-upload-step-title">
                      {currentStepInfo.title || `Step ${currentStepInfo.position}`}
                    </h2>
                  </>
                ) : (
                  <h2 className="guide-upload-step-title">No step selected yet</h2>
                )}
                <p className="guide-upload-step-context">
                  {hasLiveEditorSession
                    ? (
                      currentStepInfo
                        ? 'Images from this device will be attached to the selected step in the live guide draft.'
                        : 'The live editor session is ready. Create the first step to start uploading images.'
                    )
                    : 'The uploader will unlock once the main guide is open in edit mode on the other device.'
                  }
                </p>
              </div>
            </div>

            {hasLiveEditorSession && !hasLiveSteps ? (
              <div className="guide-upload-empty">
                <p className="guide-upload-step-context">
                  This guide does not have any steps yet.
                </p>
                <button
                  type="button"
                  className="btn btn-outline-dark guide-upload-secondary-button"
                  onClick={handleAddStepBelow}
                  disabled={!canAddStep}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Create first step
                </button>
              </div>
            ) : (
              <>
                <div className="guide-upload-button-grid">
                  <button
                    type="button"
                    className="btn btn-outline-dark guide-upload-secondary-button"
                    onClick={() => handleMoveSelection('up')}
                    disabled={!canMoveUp}
                  >
                    <FontAwesomeIcon icon={faArrowUp} className="me-2" />
                    Move up
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-dark guide-upload-secondary-button"
                    onClick={() => handleMoveSelection('down')}
                    disabled={!canMoveDown}
                  >
                    <FontAwesomeIcon icon={faArrowDown} className="me-2" />
                    Move down
                  </button>
                </div>

                <div className="guide-upload-action-grid mt-3">
                  <button
                    type="button"
                    className="btn btn-outline-dark guide-upload-secondary-button"
                    onClick={handleAddStepBelow}
                    disabled={!canAddStep}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Add step below
                  </button>
                  <button
                    type="button"
                    className="btn guide-upload-secondary-button guide-upload-danger-button"
                    onClick={handleDeleteCurrentStep}
                    disabled={!canDeleteCurrentStep}
                  >
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Delete current step
                  </button>
                </div>
              </>
            )}
          </section>

          <section className="guide-upload-card">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleChangeFile}
              style={{ display: 'none' }}
            />
            <button
              onClick={showFileDialog}
              type="button"
              className="btn btn-dark guide-upload-upload-button"
              disabled={!canUpload}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Uploading image(s)...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCamera} className="me-2" />
                  Upload image(s)
                </>
              )}
            </button>
            <p className="guide-upload-hint">
              Use your phone camera or photo library. Multiple images are supported, and they will be added to the selected step in the live editor.
            </p>
            {uploadResult.complete ? (
              <div className={`alert alert-${uploadResult.class} mb-0`} role="alert">
                {uploadResult.message}
              </div>
            ) : null}
          </section>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default GuideHomeUpload;
